// ============================================================================
// DEVICE FINGERPRINT SERVICE - Multi-Account Detection
// ============================================================================
// This service generates device fingerprints and tracks which users
// have been seen on which devices. Creates FLAGS when multiple accounts
// are detected on the same device.
// ============================================================================

import {
  collection,
  doc,
  setDoc,
  getDoc,
  updateDoc,
  query,
  where,
  getDocs,
  Timestamp,
  arrayUnion,
} from 'firebase/firestore';
import { db } from '../firebase';
import {
  DeviceFingerprint,
  FingerprintComponents,
  RawFingerprintData,
  SecurityContext,
  IPHistory,
  FLAG_THRESHOLDS,
} from '../../types/securityTypes';
import securityFlagService from './securityFlagService';

// ============================================================================
// CONSTANTS
// ============================================================================

const FINGERPRINT_COLLECTION = 'deviceFingerprints';
const IP_HISTORY_COLLECTION = 'ipHistory';
const USER_SECURITY_COLLECTION = 'userSecurity';

// ============================================================================
// DEVICE FINGERPRINT SERVICE CLASS
// ============================================================================

class DeviceFingerprintService {
  // ============================================================================
  // FINGERPRINT GENERATION (Client-side helper)
  // ============================================================================

  /**
   * Generate a device fingerprint from browser data
   * This should be called on the client and the hash sent to server
   */
  async generateFingerprint(): Promise<{ hash: string; components: FingerprintComponents }> {
    const components = await this.collectFingerprintComponents();
    const hash = await this.hashFingerprint(components);
    
    return { hash, components };
  }

  /**
   * Collect fingerprint components from browser
   */
  private async collectFingerprintComponents(): Promise<FingerprintComponents> {
    // Basic components (always available)
    const components: FingerprintComponents = {
      userAgent: navigator.userAgent || 'unknown',
      screenResolution: `${window.screen.width}x${window.screen.height}`,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'unknown',
      language: navigator.language || 'unknown',
      platform: navigator.platform || 'unknown',
      colorDepth: window.screen.colorDepth || 24,
      hardwareConcurrency: navigator.hardwareConcurrency || 4,
    };

    // Canvas fingerprint (more unique)
    try {
      components.canvasHash = this.getCanvasFingerprint();
    } catch (e) {
      console.log('Canvas fingerprint unavailable');
    }

    // WebGL fingerprint
    try {
      const webgl = this.getWebGLFingerprint();
      if (webgl) {
        components.webglVendor = webgl.vendor;
        components.webglRenderer = webgl.renderer;
      }
    } catch (e) {
      console.log('WebGL fingerprint unavailable');
    }

    return components;
  }

  /**
   * Generate canvas fingerprint
   */
  private getCanvasFingerprint(): string {
    try {
      const canvas = document.createElement('canvas');
      canvas.width = 200;
      canvas.height = 50;
      const ctx = canvas.getContext('2d');
      
      if (!ctx) return 'unavailable';

      // Draw text with various styles
      ctx.textBaseline = 'top';
      ctx.font = '14px Arial';
      ctx.fillStyle = '#f60';
      ctx.fillRect(125, 1, 62, 20);
      ctx.fillStyle = '#069';
      ctx.fillText('PokerSweepStars', 2, 15);
      ctx.fillStyle = 'rgba(102, 204, 0, 0.7)';
      ctx.fillText('Canvas FP', 4, 35);

      return canvas.toDataURL();
    } catch (e) {
      return 'error';
    }
  }

  /**
   * Get WebGL vendor and renderer
   */
  private getWebGLFingerprint(): { vendor: string; renderer: string } | null {
    try {
      const canvas = document.createElement('canvas');
      const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl') as WebGLRenderingContext | null;
      
      if (!gl) return null;

      const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
      if (!debugInfo) return null;

      return {
        vendor: gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL) || 'unknown',
        renderer: gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) || 'unknown',
      };
    } catch (e) {
      return null;
    }
  }

  /**
   * Hash fingerprint components using SHA-256
   */
  private async hashFingerprint(components: FingerprintComponents): Promise<string> {
    const data = JSON.stringify({
      ua: components.userAgent,
      sr: components.screenResolution,
      tz: components.timezone,
      lang: components.language,
      plat: components.platform,
      cd: components.colorDepth,
      hc: components.hardwareConcurrency,
      canvas: components.canvasHash?.substring(0, 100), // Truncate for consistency
      webgl: `${components.webglVendor}|${components.webglRenderer}`,
    });

    // Use Web Crypto API for SHA-256
    const encoder = new TextEncoder();
    const dataBuffer = encoder.encode(data);
    const hashBuffer = await crypto.subtle.digest('SHA-256', dataBuffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    
    return hashHex;
  }

  /**
   * Hash an IP address for privacy
   */
  async hashIP(ip: string): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(`pss_ip_${ip}`); // Salt with prefix
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  }

  // ============================================================================
  // DEVICE LINKING & DETECTION
  // ============================================================================

  /**
   * Link a device fingerprint to a user
   * Called at signup and login
   */
  async linkDeviceToUser(
    userId: string,
    fingerprintHash: string,
    components: FingerprintComponents
  ): Promise<{ isNewDevice: boolean; otherUsers: string[] }> {
    try {
      console.log(`🔗 [DeviceFP] Linking device to user ${userId.substring(0, 8)}`);

      const fpRef = doc(db, FINGERPRINT_COLLECTION, fingerprintHash);
      const fpDoc = await getDoc(fpRef);

      let isNewDevice = true;
      let otherUsers: string[] = [];

      if (fpDoc.exists()) {
        // Device already seen
        const fpData = fpDoc.data() as DeviceFingerprint;
        isNewDevice = false;
        
        // Get other users who have used this device
        otherUsers = fpData.userIds.filter(id => id !== userId);
        
        // Add this user if not already in list
        if (!fpData.userIds.includes(userId)) {
          await updateDoc(fpRef, {
            userIds: arrayUnion(userId),
            lastSeenAt: Timestamp.now(),
          });

          // Check for multi-account violation
          const totalUsers = fpData.userIds.length + 1;
          await this.checkAndFlagMultiAccount(userId, otherUsers, fingerprintHash, totalUsers);
        } else {
          // Just update last seen
          await updateDoc(fpRef, {
            lastSeenAt: Timestamp.now(),
          });
        }

        console.log(`ℹ️ [DeviceFP] Known device - ${otherUsers.length} other users`);
      } else {
        // New device - create record
        const newFP: DeviceFingerprint = {
          hash: fingerprintHash,
          userIds: [userId],
          components,
          firstSeenAt: Timestamp.now(),
          lastSeenAt: Timestamp.now(),
          firstSeenUserId: userId,
          flagged: false,
        };

        await setDoc(fpRef, newFP);
        console.log(`✅ [DeviceFP] New device registered`);
      }

      // Also update user's security profile
      await this.updateUserSecurityProfile(userId, fingerprintHash);

      return { isNewDevice, otherUsers };

    } catch (error) {
      console.error('❌ [DeviceFP] Error linking device:', error);
      return { isNewDevice: true, otherUsers: [] };
    }
  }

  /**
   * Check for duplicate accounts and create flags if needed
   */
  private async checkAndFlagMultiAccount(
    userId: string,
    otherUserIds: string[],
    fingerprintHash: string,
    totalUsers: number
  ): Promise<void> {
    if (otherUserIds.length === 0) return;

    console.log(`⚠️ [DeviceFP] Multi-account detected: ${totalUsers} users on same device`);

    // Determine confidence and severity based on number of accounts
    let confidence: number;
    let threshold: number;

    if (totalUsers >= 3) {
      confidence = 95;
      threshold = FLAG_THRESHOLDS.SAME_DEVICE_3_PLUS_ACCOUNTS;
    } else {
      confidence = 85;
      threshold = FLAG_THRESHOLDS.SAME_DEVICE_2_ACCOUNTS;
    }

    // Check if flag already exists for this pair
    const primaryOther = otherUserIds[0];
    const hasExisting = await securityFlagService.hasExistingFlag(
      'multi_account',
      userId,
      primaryOther,
      168 // 1 week
    );

    if (hasExisting) {
      console.log(`ℹ️ [DeviceFP] Flag already exists for this user pair`);
      return;
    }

    // Create flag
    await securityFlagService.createFlagIfThresholdMet(
      {
        type: 'multi_account',
        severity: totalUsers >= 3 ? 'critical' : 'high',
        confidence,
        primaryUserId: userId,
        relatedUserId: primaryOther,
        evidence: {
          signals: ['SAME_DEVICE_FINGERPRINT'],
          dataPoints: [
            {
              type: 'device_match',
              description: `${totalUsers} accounts on same device`,
              value: fingerprintHash.substring(0, 16) + '...',
            },
            {
              type: 'other_accounts',
              description: 'Other user IDs on this device',
              value: otherUserIds.map(id => id.substring(0, 8)).join(', '),
            },
          ],
          summary: `${totalUsers} accounts detected on same device fingerprint`,
        },
      },
      threshold
    );

    // Mark fingerprint as flagged
    const fpRef = doc(db, FINGERPRINT_COLLECTION, fingerprintHash);
    await updateDoc(fpRef, { flagged: true });
  }

  // ============================================================================
  // IP TRACKING
  // ============================================================================

  /**
   * Record IP for a user
   * Called at signup and login
   */
  async recordUserIP(
    userId: string,
    ip: string
  ): Promise<{ isNewIP: boolean; otherUsers: string[] }> {
    try {
      const ipHash = await this.hashIP(ip);
      console.log(`📍 [DeviceFP] Recording IP for user ${userId.substring(0, 8)}`);

      const ipRef = doc(db, IP_HISTORY_COLLECTION, ipHash);
      const ipDoc = await getDoc(ipRef);

      let isNewIP = true;
      let otherUsers: string[] = [];

      if (ipDoc.exists()) {
        const ipData = ipDoc.data() as IPHistory;
        isNewIP = false;
        otherUsers = ipData.userIds.filter(id => id !== userId);

        if (!ipData.userIds.includes(userId)) {
          await updateDoc(ipRef, {
            userIds: arrayUnion(userId),
            lastSeenAt: Timestamp.now(),
          });
        } else {
          await updateDoc(ipRef, {
            lastSeenAt: Timestamp.now(),
          });
        }
      } else {
        const newIP: IPHistory = {
          ipHash,
          userIds: [userId],
          tableJoins: [],
          firstSeenAt: Timestamp.now(),
          lastSeenAt: Timestamp.now(),
          flagged: false,
        };

        await setDoc(ipRef, newIP);
      }

      return { isNewIP, otherUsers };

    } catch (error) {
      console.error('❌ [DeviceFP] Error recording IP:', error);
      return { isNewIP: true, otherUsers: [] };
    }
  }

  /**
   * Check if multiple users at same table have same IP
   */
  async checkSameIPAtTable(
    tableId: string,
    playerId: string,
    currentPlayerIds: string[],
    playerIpHash: string
  ): Promise<string[]> {
    try {
      // Get IPs for all current players at table
      const sameIPPlayers: string[] = [];

      for (const otherId of currentPlayerIds) {
        if (otherId === playerId) continue;

        // Check if other player has same IP
        const otherSecurity = await getDoc(doc(db, USER_SECURITY_COLLECTION, otherId));
        if (otherSecurity.exists()) {
          const data = otherSecurity.data();
          if (data.lastLoginIpHash === playerIpHash) {
            sameIPPlayers.push(otherId);
          }
        }
      }

      if (sameIPPlayers.length > 0) {
        console.log(`⚠️ [DeviceFP] Same IP at table: ${playerId.substring(0, 8)} + ${sameIPPlayers.length} others`);

        // Create flag for same IP at table
        for (const otherId of sameIPPlayers) {
          const hasExisting = await securityFlagService.hasExistingFlag(
            'same_ip_table',
            playerId,
            otherId,
            2 // 2 hours
          );

          if (!hasExisting) {
            await securityFlagService.createFlagIfThresholdMet(
              {
                type: 'same_ip_table',
                severity: 'medium',
                confidence: FLAG_THRESHOLDS.SAME_IP_TABLE,
                primaryUserId: playerId,
                relatedUserId: otherId,
                evidence: {
                  signals: ['SAME_IP_AT_TABLE'],
                  dataPoints: [
                    {
                      type: 'table_id',
                      description: 'Table where same IP detected',
                      value: tableId,
                    },
                  ],
                  tableIds: [tableId],
                  summary: `Two players at same table with same IP address`,
                },
              },
              FLAG_THRESHOLDS.SAME_IP_TABLE
            );
          }
        }
      }

      return sameIPPlayers;

    } catch (error) {
      console.error('❌ [DeviceFP] Error checking same IP at table:', error);
      return [];
    }
  }

  // ============================================================================
  // USER SECURITY PROFILE
  // ============================================================================

  /**
   * Update user's security profile with device info
   */
  private async updateUserSecurityProfile(
    userId: string,
    fingerprintHash: string,
    ipHash?: string
  ): Promise<void> {
    try {
      const securityRef = doc(db, USER_SECURITY_COLLECTION, userId);
      const securityDoc = await getDoc(securityRef);

      if (securityDoc.exists()) {
        const updates: any = {
          lastLoginDeviceFingerprint: fingerprintHash,
          lastLoginAt: Timestamp.now(),
        };

        // Add to known devices if not already there
        const data = securityDoc.data();
        if (!data.knownDevices?.includes(fingerprintHash)) {
          updates.knownDevices = arrayUnion(fingerprintHash);
        }

        if (ipHash) {
          updates.lastLoginIpHash = ipHash;
          if (!data.knownIps?.includes(ipHash)) {
            updates.knownIps = arrayUnion(ipHash);
          }
        }

        await updateDoc(securityRef, updates);
      } else {
        // Create new security profile
        await setDoc(securityRef, {
          userId,
          signupDeviceFingerprint: fingerprintHash,
          signupAt: Timestamp.now(),
          lastLoginDeviceFingerprint: fingerprintHash,
          lastLoginAt: Timestamp.now(),
          knownDevices: [fingerprintHash],
          knownIps: ipHash ? [ipHash] : [],
          lastLoginIpHash: ipHash || null,
          flagCount: 0,
          activeFlags: 0,
        });
      }

    } catch (error) {
      console.error('❌ [DeviceFP] Error updating security profile:', error);
    }
  }

  /**
   * Get security profile for a user
   */
  async getUserSecurityProfile(userId: string): Promise<any | null> {
    try {
      const securityRef = doc(db, USER_SECURITY_COLLECTION, userId);
      const securityDoc = await getDoc(securityRef);

      if (securityDoc.exists()) {
        return securityDoc.data();
      }

      return null;

    } catch (error) {
      console.error('❌ [DeviceFP] Error getting security profile:', error);
      return null;
    }
  }

  /**
   * Check for anomalies on login (new device, new IP)
   */
  async checkForLoginAnomalies(
    userId: string,
    fingerprintHash: string,
    ipHash?: string
  ): Promise<{ newDevice: boolean; newIP: boolean; suspicious: boolean }> {
    try {
      const profile = await this.getUserSecurityProfile(userId);

      if (!profile) {
        // First login, nothing to compare
        return { newDevice: false, newIP: false, suspicious: false };
      }

      const newDevice = !profile.knownDevices?.includes(fingerprintHash);
      const newIP = ipHash ? !profile.knownIps?.includes(ipHash) : false;
      
      // Consider suspicious if both new device AND new IP
      const suspicious = newDevice && newIP;

      if (suspicious) {
        console.log(`⚠️ [DeviceFP] Suspicious login: new device + new IP for ${userId.substring(0, 8)}`);
      }

      return { newDevice, newIP, suspicious };

    } catch (error) {
      console.error('❌ [DeviceFP] Error checking login anomalies:', error);
      return { newDevice: false, newIP: false, suspicious: false };
    }
  }

  // ============================================================================
  // QUERIES
  // ============================================================================

  /**
   * Get all users who have used a specific device
   */
  async getUsersForDevice(fingerprintHash: string): Promise<string[]> {
    try {
      const fpRef = doc(db, FINGERPRINT_COLLECTION, fingerprintHash);
      const fpDoc = await getDoc(fpRef);

      if (fpDoc.exists()) {
        return (fpDoc.data() as DeviceFingerprint).userIds;
      }

      return [];

    } catch (error) {
      console.error('❌ [DeviceFP] Error getting users for device:', error);
      return [];
    }
  }

  /**
   * Get all devices a user has used
   */
  async getDevicesForUser(userId: string): Promise<string[]> {
    try {
      const profile = await this.getUserSecurityProfile(userId);
      return profile?.knownDevices || [];

    } catch (error) {
      console.error('❌ [DeviceFP] Error getting devices for user:', error);
      return [];
    }
  }

  /**
   * Check if two users share any devices
   */
  async doUsersShareDevice(userId1: string, userId2: string): Promise<boolean> {
    try {
      const devices1 = await this.getDevicesForUser(userId1);
      const devices2 = await this.getDevicesForUser(userId2);

      for (const device of devices1) {
        if (devices2.includes(device)) {
          return true;
        }
      }

      return false;

    } catch (error) {
      console.error('❌ [DeviceFP] Error checking shared device:', error);
      return false;
    }
  }

  /**
   * Check if two users share any IPs
   */
  async doUsersShareIP(userId1: string, userId2: string): Promise<boolean> {
    try {
      const profile1 = await this.getUserSecurityProfile(userId1);
      const profile2 = await this.getUserSecurityProfile(userId2);

      if (!profile1 || !profile2) return false;

      const ips1 = profile1.knownIps || [];
      const ips2 = profile2.knownIps || [];

      for (const ip of ips1) {
        if (ips2.includes(ip)) {
          return true;
        }
      }

      return false;

    } catch (error) {
      console.error('❌ [DeviceFP] Error checking shared IP:', error);
      return false;
    }
  }

  // ============================================================================
  // SECURITY CONTEXT CAPTURE
  // ============================================================================

  /**
   * Capture full security context at signup/login
   * This bundles fingerprint + IP + other data
   */
  async captureSecurityContext(ip?: string): Promise<SecurityContext> {
    const { hash, components } = await this.generateFingerprint();
    
    let ipHash = '';
    if (ip) {
      ipHash = await this.hashIP(ip);
    }

    return {
      ip: ip || '',
      ipHash,
      userAgent: navigator.userAgent,
      deviceFingerprint: hash,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      screenResolution: `${window.screen.width}x${window.screen.height}`,
      language: navigator.language,
      platform: navigator.platform,
      timestamp: Date.now(),
    };
  }

  /**
   * Process security context for a user (called after signup/login)
   */
  async processSecurityContext(
    userId: string,
    context: SecurityContext
  ): Promise<void> {
    try {
      console.log(`🔐 [DeviceFP] Processing security context for ${userId.substring(0, 8)}`);

      // Link device
      const components: FingerprintComponents = {
        userAgent: context.userAgent,
        screenResolution: context.screenResolution,
        timezone: context.timezone,
        language: context.language,
        platform: context.platform,
        colorDepth: window.screen.colorDepth,
        hardwareConcurrency: navigator.hardwareConcurrency,
      };

      await this.linkDeviceToUser(userId, context.deviceFingerprint, components);

      // Record IP if available
      if (context.ip) {
        await this.recordUserIP(userId, context.ip);
      }

      // Update security profile
      await this.updateUserSecurityProfile(userId, context.deviceFingerprint, context.ipHash);

      console.log(`✅ [DeviceFP] Security context processed`);

    } catch (error) {
      console.error('❌ [DeviceFP] Error processing security context:', error);
    }
  }

  /**
   * Get current user's IP address and return its hash
   * Uses public IP lookup service (fallback to empty string on failure)
   */
  async getCurrentIpHash(): Promise<string> {
    try {
      // Try to get IP from a public API
      // Using ipify as it's free, reliable, and simple
      const response = await fetch('https://api.ipify.org?format=json', {
        method: 'GET',
        headers: { 'Accept': 'application/json' },
      });
      
      if (!response.ok) {
        console.warn('⚠️ [DeviceFP] Failed to fetch IP');
        return '';
      }
      
      const data = await response.json();
      const ip = data.ip;
      
      if (ip) {
        return await this.hashIP(ip);
      }
      
      return '';
    } catch (error) {
      console.warn('⚠️ [DeviceFP] Error getting current IP hash:', error);
      return '';
    }
  }
}

// ============================================================================
// SINGLETON EXPORT
// ============================================================================

const deviceFingerprintService = new DeviceFingerprintService();
export { deviceFingerprintService };
export default deviceFingerprintService;